import type { Family } from '@/lib/newsletterEmail'
import { listMissing, isEmail, type NewsletterList } from '@/hooks/useNewsletter'

/**
 * The send checklist (Brian's sendGate() in after/newsletter.html, Oct 29:
 * GoGrov sends, so the first step is Your list, not Mailchimp). Send stays
 * locked until every must-have is done: your list ready (subscribers, a
 * reply-to and a mailing address), your own stories, a subject line and a test
 * to yourself. Your note is good to have; it is flagged while it is still the
 * example.
 */

/** The Letter's example note: the builder starts with it, and the checklist
 *  flags it until the creator writes their own. */
export const exampleNote = (town: string) =>
  `Here’s what’s happening around ${town || 'town'} this week: the stories I’m watching, and a few I couldn’t fit on camera.`
export const EXAMPLE_NOTE_RE = /a few I couldn.t fit on camera/

export interface SendStep {
  key: 'list' | 'stories' | 'subject' | 'test' | 'note' | 'sponsors'
  label: string
  note: string
  done: boolean
  must: boolean
  /** Information only (the Letter's no-ad-space line). */
  info?: boolean
  /** The step's own button, if it has one. */
  fix?: string
}

export interface GateInput {
  family: Family
  list: NewsletterList | null | undefined
  /** People subscribed on Your list. */
  subscribers: number
  example: boolean
  storyCount: number
  subject: string
  note: string
  noteOn: boolean
  /** Where the last test went, and whether it still matches the issue. */
  test: { to: string | null; current: boolean }
}

/** Brian's NL.missing: what Your list still needs, in words for the checklist. */
const MISSING: Record<string, string> = {
  'Add subscribers': 'Add subscribers to your list',
  'Needs reply-to': 'Add a reply-to address',
  'Needs mailing address': 'Add the mailing address the law asks for',
}

export function sendGate(g: GateInput) {
  const missing = listMissing(g.list, g.subscribers)
  const ready0 = !missing
  const people = g.subscribers ? g.subscribers.toLocaleString() : ''
  const subject = g.subject.trim()
  const note = g.noteOn ? g.note.trim() : ''
  const sampleNote = EXAMPLE_NOTE_RE.test(note)
  const tested = !!g.test.to && g.test.current
  const steps: SendStep[] = [
    {
      key: 'list', must: true, done: ready0,
      label: ready0 ? `Your list: ${people} people` : MISSING[missing] ?? 'Set up your list',
      note: ready0
        ? `Sends from GoGrov${isEmail(g.list?.reply_to) ? ` · replies go to ${g.list!.reply_to}` : ''}`
        : 'Your list lives in GoGrov: subscribers, a reply-to address and your mailing address.',
      fix: ready0 ? undefined : 'Your list',
    },
    {
      key: 'stories', must: true, done: !g.example && g.storyCount > 0,
      label: g.example ? 'Add your own stories' : `${g.storyCount} of your stories in this issue`,
      note: g.example
        ? 'These are example stories. Tap ＋ Newsletter on a story in your feed and it replaces them.'
        : 'Each one rewritten for the newsletter.',
      fix: g.example || g.storyCount === 0 ? 'Your feed' : undefined,
    },
    {
      key: 'subject', must: true, done: !!subject,
      label: 'A subject line',
      note: subject ? `“${subject}”` : 'What people see in their inbox. Pick a suggestion above or write your own.',
    },
    {
      key: 'test', must: true, done: tested,
      label: 'Send a test to yourself',
      note: tested
        ? `Sent to ${g.test.to || 'your email'}. Check it looks right.`
        : g.test.to
          ? 'You changed the issue after your test. Send another so you see what your list will get.'
          : 'See it in your own inbox before your list does.',
      fix: g.test.to ? 'Send again' : 'Send test',
    },
    {
      key: 'note', must: false, done: g.family !== 'letter' || (!!note && !sampleNote),
      label: 'Your note, in your words',
      note: g.family !== 'letter'
        ? 'Optional in this design.'
        : sampleNote ? 'It still has the example note. Change it so it sounds like you.'
          : note ? 'Ready.' : 'A line or two in your own words.',
    },
  ]
  if (g.family === 'letter') {
    steps.push({
      key: 'sponsors', must: false, done: true, info: true, label: 'Sponsors',
      note: 'The Letter has no ad space. To include a sponsor, switch to The Magazine or The Bulletin.',
    })
  }
  const ready = steps.every((x) => !x.must || x.done)
  return {
    steps,
    ready,
    missing,
    label: ready ? (people ? `Send to ${people} people now` : 'Send now') : 'Send',
    warn: ready ? 'Once it’s sent, it can’t be unsent.' : 'Send unlocks when the circles are done.',
    line: (ready0 ? `Sends from GoGrov to your list (${people})` : `${MISSING[missing] ?? 'Set up your list'} (Your list)`)
      + ` · ${g.storyCount} stories placed · GoGrov adds your mailing address and the unsubscribe link`,
  }
}
